import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'reactstrap';
import Navbar from './Navbar';
import Footer from './Footer';
import GuestChatWidget from './GuestChatWidget';
import './landing.scss';
import SeoHead from '../../../Components/Common/SeoHead';
import HelpBreadcrumbSchema from '../../../Components/Common/HelpBreadcrumbSchema';
import HelpScreenshot from './HelpScreenshot';
import type { HelpImage } from './HelpScreenshot';

const HelpArticleGmailTracking: React.FC = () => {
  const BASE = '/images/help/gmail-tracking';

  const screenshots: Record<string, HelpImage[]> = {
    step1: [
      { src: `${BASE}/gt-step-1.png`, alt: 'Gmail tracking overview', caption: 'Step 1 – How Coderom reads tracking numbers from your Amazon emails' },
    ],
    step2: [
      { src: `${BASE}/gt-step-2.png`, alt: 'Gmail tracking flow diagram', caption: 'Step 2 – The end-to-end Gmail tracking flow' },
    ],
    step3: [
      { src: `${BASE}/gt-step-3.png`, alt: 'Gmail App Password connection screen', caption: 'Step 3 – Connect your Gmail account with an App Password' },
    ],
    step4: [
      { src: `${BASE}/gt-step-4.png`, alt: 'Supported carriers list', caption: 'Step 4 – Supported carriers and tracking number formats' },
    ],
    step5: [
      { src: `${BASE}/gt-step-5.png`, alt: 'Tracking status in Orders page', caption: 'Step 5 – Monitor tracking status from the Orders page' },
    ],
    step6: [
      { src: `${BASE}/gt-step-6.png`, alt: 'Gmail tracking troubleshooting', caption: 'Step 6 – Troubleshoot common Gmail tracking issues' },
    ],
  };

  return (
    <div className="layout-wrapper landing">
      <SeoHead
        title="Gmail Integration & Tracking | CodeRom Help"
        description="Connect Gmail to CodeRom for automatic Amazon tracking number updates on eBay."
      />
      <HelpBreadcrumbSchema slug="gmail-tracking" name="Gmail integration & tracking" />
      <Navbar />

      <div className="help-article-page">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} xl={8}>
              {/* Breadcrumb */}
              <div className="breadcrumb-section">
                <Link to="/help" className="breadcrumb-link">Help Center</Link>
                <span className="breadcrumb-separator">›</span>
                <span className="breadcrumb-current">Gmail integration & tracking</span>
              </div>

              {/* Article Header */}
              <div className="article-header">
                <h1>Gmail integration & tracking</h1>
                <p className="article-subtitle">
                  Connect your Gmail account to automatically capture Amazon dispatch emails and upload tracking numbers to eBay. No more manual tracking entry.
                </p>
                <div className="article-meta">
                  <span className="read-time">📖 7 min read</span>
                  <span className="category">Tracking</span>
                </div>
              </div>

              {/* Table of Contents */}
              <div className="table-of-contents">
                <h3>📋 On this page</h3>
                <ul>
                  <li><a href="#step1">1. What is Gmail tracking?</a></li>
                  <li><a href="#step2">2. How it works</a></li>
                  <li><a href="#step3">3. Connect your Gmail account</a></li>
                  <li><a href="#step4">4. Supported carriers</a></li>
                  <li><a href="#step5">5. Tracking status and monitoring</a></li>
                  <li><a href="#step6">6. Troubleshooting</a></li>
                </ul>
              </div>

              {/* Article Content */}
              <div className="article-content">

                {/* Step 1 */}
                <div id="step1" className="content-section">
                  <h2>Step 1: What is Gmail tracking?</h2>
                  <p>
                    When Amazon dispatches an order, they send a confirmation email to your Gmail. Coderom's Gmail integration reads these emails automatically and:
                  </p>
                  <ul>
                    <li>Extracts the tracking number</li>
                    <li>Matches it to the correct eBay order</li>
                    <li>Uploads the tracking to eBay</li>
                    <li>Marks the order as shipped</li>
                  </ul>
                  <p>
                    This eliminates the need to manually copy tracking numbers from Amazon emails.
                  </p>

                  <HelpScreenshot images={screenshots.step1} stepLabel="Step 1: What is Gmail tracking?" />
                </div>

                {/* Step 2 */}
                <div id="step2" className="content-section">
                  <h2>Step 2: How it works</h2>
                  <p>
                    The process is fully automated:
                  </p>
                  <ol>
                    <li><strong>Amazon dispatches your order</strong> and sends a "Your order has been dispatched" email</li>
                    <li><strong>Coderom checks your Gmail</strong> every few hours for new dispatch emails</li>
                    <li><strong>Tracking number extracted</strong> from the email content</li>
                    <li><strong>Order matched</strong> — Coderom links the tracking to the correct eBay order using the Amazon Order ID</li>
                    <li><strong>eBay updated</strong> — tracking number and carrier are uploaded to eBay</li>
                    <li><strong>Buyer notified</strong> — eBay sends the buyer a dispatch notification</li>
                  </ol>

                  <HelpScreenshot images={screenshots.step2} stepLabel="Step 2: How it works" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> Make sure your Amazon order confirmation emails go to the Gmail account you connect to Coderom. If you use a different email for Amazon, set up email forwarding.
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div id="step3" className="content-section">
                  <h2>Step 3: Connect your Gmail account</h2>
                  <ol>
                    <li>Enable <strong>2-Step Verification</strong> on your Google account (required for App Passwords)</li>
                    <li>Go to <strong><code>myaccount.google.com/apppasswords</code></strong>, enter an app name, and click <strong>Create</strong></li>
                    <li>Copy the 16-character App Password that Google shows you</li>
                    <li>In Coderom, open <strong>Settings → Email Integration</strong></li>
                    <li>Enter your Gmail address, paste the App Password, then click <strong>Connect</strong> (read-only — Coderom cannot send or delete emails)</li>
                    <li>Once connected, you'll see a "Connected" status with your email address</li>
                  </ol>

                  <HelpScreenshot images={screenshots.step3} stepLabel="Step 3: Connect your Gmail account" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> Coderom uses read-only access. It can only read your emails to find tracking information — it cannot send, modify, or delete any emails.
                    </div>
                  </div>
                </div>

                {/* Step 4 */}
                <div id="step4" className="content-section">
                  <h2>Step 4: Supported carriers</h2>
                  <p>
                    Coderom recognises tracking numbers from these carriers:
                  </p>

                  <div className="carriers-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Carrier</th>
                          <th>Region</th>
                          <th>Tracking format</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Royal Mail</strong></td>
                          <td>UK</td>
                          <td>2-letter + 9 digits + GB</td>
                        </tr>
                        <tr>
                          <td><strong>DPD</strong></td>
                          <td>UK/EU</td>
                          <td>14-digit number</td>
                        </tr>
                        <tr>
                          <td><strong>Hermes/Evri</strong></td>
                          <td>UK</td>
                          <td>16-digit number</td>
                        </tr>
                        <tr>
                          <td><strong>Amazon Logistics</strong></td>
                          <td>UK/US/EU</td>
                          <td>TBA + digits</td>
                        </tr>
                        <tr>
                          <td><strong>USPS</strong></td>
                          <td>US</td>
                          <td>20-22 digit number</td>
                        </tr>
                        <tr>
                          <td><strong>UPS</strong></td>
                          <td>Global</td>
                          <td>1Z + 16 characters</td>
                        </tr>
                        <tr>
                          <td><strong>FedEx</strong></td>
                          <td>Global</td>
                          <td>12-15 digit number</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <HelpScreenshot images={screenshots.step4} stepLabel="Step 4: Supported carriers" />
                </div>

                {/* Step 5 */}
                <div id="step5" className="content-section">
                  <h2>Step 5: Tracking status and monitoring</h2>
                  <p>
                    Monitor your tracking status from the <strong>Orders</strong> page:
                  </p>

                  <div className="status-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Status</th>
                          <th>Meaning</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Awaiting tracking</strong></td>
                          <td>Order shipped, waiting for Gmail email</td>
                        </tr>
                        <tr>
                          <td><strong>Tracking found</strong></td>
                          <td>Tracking extracted from email</td>
                        </tr>
                        <tr>
                          <td><strong>Uploaded to eBay</strong></td>
                          <td>Tracking successfully sent to eBay</td>
                        </tr>
                        <tr>
                          <td><strong>Failed</strong></td>
                          <td>Could not extract or upload tracking — needs manual review</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <p>
                    Go to <strong>Orders</strong> and filter by tracking status to see which orders need attention.
                  </p>

                  <HelpScreenshot images={screenshots.step5} stepLabel="Step 5: Tracking status and monitoring" />
                </div>

                {/* Step 6 */}
                <div id="step6" className="content-section">
                  <h2>Step 6: Troubleshooting</h2>

                  <div className="troubleshooting-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Issue</th>
                          <th>Cause</th>
                          <th>Solution</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>"No tracking found"</strong></td>
                          <td>Amazon email not yet received</td>
                          <td>Wait a few hours — Amazon emails can be delayed</td>
                        </tr>
                        <tr>
                          <td><strong>"Gmail not connected"</strong></td>
                          <td>App Password invalid or revoked (e.g. 2-Step Verification turned off)</td>
                          <td>Generate a new App Password and reconnect in Settings → Email Integration</td>
                        </tr>
                        <tr>
                          <td><strong>"Carrier not recognised"</strong></td>
                          <td>Unusual tracking format</td>
                          <td>Enter tracking manually on the order page</td>
                        </tr>
                        <tr>
                          <td><strong>"Wrong order matched"</strong></td>
                          <td>Multiple similar orders</td>
                          <td>Check the Amazon Order ID matches correctly</td>
                        </tr>
                        <tr>
                          <td><strong>Emails going to spam</strong></td>
                          <td>Gmail filtering</td>
                          <td>Add Amazon to your contacts or check spam folder</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <HelpScreenshot images={screenshots.step6} stepLabel="Step 6: Troubleshooting" />
                </div>

                {/* What's Next */}
                <div className="whats-next-section">
                  <h2>What's next?</h2>
                  <p>Now that Gmail tracking is set up, explore these features:</p>
                  <div className="next-steps">
                    <Link to="/help/auto-messages" className="next-step-card">
                      <div className="next-step-icon">💬</div>
                      <div className="next-step-content">
                        <h4>Automated buyer messages</h4>
                        <p>Send order confirmations and delivery updates automatically</p>
                      </div>
                    </Link>
                    <Link to="/help/reports" className="next-step-card">
                      <div className="next-step-icon">📊</div>
                      <div className="next-step-content">
                        <h4>Daily reports & notifications</h4>
                        <p>Get daily summaries of sales, profit, and inventory updates</p>
                      </div>
                    </Link>
                    <Link to="/help/ebay-multistore" className="next-step-card">
                      <div className="next-step-icon">🏪</div>
                      <div className="next-step-content">
                        <h4>eBay policies & multi-store</h4>
                        <p>Manage multiple eBay accounts and stay compliant</p>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Was This Helpful */}
                <div className="helpful-section">
                  <h3>Was this helpful?</h3>
                  <div className="helpful-buttons">
                    <button className="helpful-btn helpful-yes">👍 Yes</button>
                    <button className="helpful-btn helpful-no">👎 No</button>
                  </div>
                  <p className="helpful-text">
                    Still need help? <Link to="/help">Browse all articles</Link> or{' '}
                    <a href="mailto:support@coderom.com">contact support</a>.
                  </p>
                </div>

              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <style>{`
        .help-article-page {
          padding: 120px 0 80px;
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
          min-height: 100vh;
        }

        /* Breadcrumb */
        .breadcrumb-section {
          margin-bottom: 24px;
          font-size: 14px;
        }
        .breadcrumb-link {
          color: #6366f1;
          text-decoration: none;
          transition: color 0.2s;
        }
        .breadcrumb-link:hover {
          color: #4f46e5;
        }
        .breadcrumb-separator {
          margin: 0 8px;
          color: #cbd5e1;
        }
        .breadcrumb-current {
          color: #64748b;
        }

        /* Article Header */
        .article-header {
          margin-bottom: 40px;
        }
        .article-header h1 {
          font-size: 42px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          line-height: 1.2;
        }
        .article-subtitle {
          font-size: 18px;
          color: #64748b;
          line-height: 1.6;
          margin-bottom: 20px;
        }
        .article-meta {
          display: flex;
          gap: 16px;
          align-items: center;
          font-size: 14px;
        }
        .read-time {
          color: #64748b;
        }
        .category {
          background: #f1f5f9;
          color: #6366f1;
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 500;
        }

        /* Table of Contents */
        .table-of-contents {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 40px;
        }
        .table-of-contents h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .table-of-contents ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .table-of-contents li {
          margin-bottom: 8px;
        }
        .table-of-contents a {
          color: #6366f1;
          text-decoration: none;
          font-size: 15px;
          transition: color 0.2s;
        }
        .table-of-contents a:hover {
          color: #4f46e5;
          text-decoration: underline;
        }

        /* Article Content */
        .article-content {
          color: #334155;
          font-size: 16px;
          line-height: 1.8;
        }
        .content-section {
          margin-bottom: 48px;
          scroll-margin-top: 100px;
        }
        .content-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          margin-top: 40px;
        }
        .content-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #334155;
          margin-top: 24px;
          margin-bottom: 12px;
        }
        .content-section p {
          margin-bottom: 16px;
        }
        .content-section ul,
        .content-section ol {
          margin-bottom: 16px;
          padding-left: 24px;
        }
        .content-section li {
          margin-bottom: 8px;
        }
        .content-section strong {
          color: #1e293b;
          font-weight: 600;
        }

        /* Tip Box */
        .tip-box {
          display: flex;
          gap: 12px;
          background: #eff6ff;
          border-left: 4px solid #3b82f6;
          padding: 16px;
          border-radius: 8px;
          margin: 24px 0;
        }
        .tip-icon {
          font-size: 20px;
          flex-shrink: 0;
        }
        .tip-content {
          font-size: 15px;
          line-height: 1.6;
          color: #1e40af;
        }

        /* Tables */
        .carriers-table,
        .status-table,
        .troubleshooting-table {
          margin: 24px 0;
          overflow-x: auto;
        }
        .carriers-table table,
        .status-table table,
        .troubleshooting-table table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .carriers-table th,
        .status-table th,
        .troubleshooting-table th {
          background: #f8fafc;
          color: #1e293b;
          font-weight: 600;
          text-align: left;
          padding: 12px 16px;
          border-bottom: 2px solid #e2e8f0;
          font-size: 14px;
        }
        .carriers-table td,
        .status-table td,
        .troubleshooting-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #f1f5f9;
          font-size: 15px;
        }
        .carriers-table tbody tr:last-child td,
        .status-table tbody tr:last-child td,
        .troubleshooting-table tbody tr:last-child td {
          border-bottom: none;
        }
        .carriers-table tbody tr:hover,
        .status-table tbody tr:hover,
        .troubleshooting-table tbody tr:hover {
          background: #f8fafc;
        }

        /* What's Next Section */
        .whats-next-section {
          margin-top: 60px;
          padding-top: 40px;
          border-top: 2px solid #e2e8f0;
        }
        .whats-next-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .next-steps {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 24px;
        }
        .next-step-card {
          display: flex;
          gap: 16px;
          padding: 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          text-decoration: none;
          transition: all 0.3s;
        }
        .next-step-card:hover {
          border-color: #6366f1;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
          transform: translateY(-2px);
        }
        .next-step-icon {
          font-size: 32px;
          flex-shrink: 0;
        }
        .next-step-content h4 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
        }
        .next-step-content p {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }

        /* Was This Helpful */
        .helpful-section {
          margin-top: 60px;
          padding: 32px;
          background: #f8fafc;
          border-radius: 12px;
          text-align: center;
        }
        .helpful-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 20px;
        }
        .helpful-buttons {
          display: flex;
          gap: 12px;
          justify-content: center;
          margin-bottom: 16px;
        }
        .helpful-btn {
          padding: 10px 24px;
          border: 2px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        .helpful-btn:hover {
          border-color: #6366f1;
          background: #eff6ff;
        }
        .helpful-text {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }
        .helpful-text a {
          color: #6366f1;
          text-decoration: none;
        }
        .helpful-text a:hover {
          text-decoration: underline;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .help-article-page {
            padding: 100px 0 60px;
          }
          .article-header h1 {
            font-size: 32px;
          }
          .article-subtitle {
            font-size: 16px;
          }
          .content-section h2 {
            font-size: 24px;
          }
          .carriers-table,
          .status-table,
          .troubleshooting-table {
            font-size: 14px;
          }
          .next-step-card {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>

      <Footer />
      <GuestChatWidget />
    </div>
  );
};

export default HelpArticleGmailTracking;
